// Historia 120 — Bloque 12 (Jupiter, paquete P6), `features/S129-avisos-grupo-lote-y-cierre-jupiter.feature`.
//
// 🔒 THE ONE resolver of the three row-level warnings -- the planning-group shortcut (HU-03/D-I8),
// the batch resume (D-I5) and the individual close of a batch member (G1-10/CA6.7) -- AND of their
// precedence (INV10/RN71.1/RN35.1): BATCH beats GROUP, and there is never more than one warning per
// click. `03-dominio.md` §7.2 #7/#8/#8-bis names this exact problem "the same one #113 solved by
// centralising the decision in one point" -- this module IS that one point, meant to be consumed
// IDENTICALLY by the THREE surfaces that can offer a start or a finish on a row-shaped job (the
// Planned Jobs tabs, All Active Jobs, and the station widgets -- S141/R-16).
//
// `resolveBatchWarningKind` is PURE and SYNCHRONOUS on purpose: `batchId`/`planningGroup` are already
// on the row (or resolved by the caller -- e.g. `common/reportOutcome.js#getJobBatchId` for the
// station widgets, S141), so precedence never needs a network round trip. The ONE candidate that DOES
// need one -- "is there really another startable member of this group HERE" (D-I8/R-9: the queue is
// paginated, the client cannot know) -- is deliberately left OUTSIDE this function: the caller calls
// N1 in `PLANNING_GROUP` mode (`batchApi.previewBatchStart`) and only THEN decides whether to actually
// show the GROUP_SHORTCUT warning, using `hasEnoughGroupMembersToOfferShortcut` below (S133/RN60).
import { JOB_ACTION_INTENT } from '../../FabricationOrderJobReference/PlannedJobs/utils/jobStatus'

export const BATCH_WARNING_KIND = {
  NONE: '',
  GROUP_SHORTCUT: 'GROUP_SHORTCUT',
  RESUME_BATCH: 'RESUME_BATCH',
  FINISH_BATCH_MEMBER: 'FINISH_BATCH_MEMBER'
}

// RN60/S133: the frontier the group shortcut needs -- "at least ONE OTHER startable job", i.e. two in
// total including the one the operator clicked. The SERVER counts (`startableCount` of N1's
// `PLANNING_GROUP`-mode response), never the client (G1-9: never the rows of the current table page).
export const MIN_GROUP_STARTABLE_COUNT = 2

// RN63-style emptiness: `null`/`undefined`/`''`/whitespace-only all read as "not present" -- the same
// treatment the group's own equality rule (`PlanningGroupKey`) already gives an empty group.
const isFilled = (value) => (typeof value === 'string' ? value.trim() !== '' : Boolean(value))

/**
 * S129-S142 — the ONE resolver. Given the row's OWN `batchId`/`planningGroup` (read verbatim, never
 * redefined -- F1/F2/F3/F4 of the frozen contract) and the operator's intent, decides which of the
 * three row-level warnings applies, or none.
 *
 * RN71.1/INV10/RN35.1 -- precedence is BATCH over GROUP, decided in a single pass: a `batchId` that is
 * present wins outright over `planningGroup` on a START (S135), and a FINISH never even LOOKS at
 * `planningGroup` (S140) -- there is no group-related warning when finishing, ever.
 *
 * S142/RN69/POL7 -- a `batchId` that never arrives (the field missing entirely, e.g. an older Sun or a
 * response shape that never carries it) reads exactly like `null`/`''`: NONE, same as today. This is
 * PREVENTION OF UX, never a guarantee of the system (RN71.2): Sun does not reject the individual close
 * of a batch member, so a missing field here simply means the operator sees no question.
 *
 * @param {{batchId?: string|null, planningGroup?: string|null}} row The minimal shape every surface
 *   can produce: the two ROW surfaces (Planned Jobs tabs, All Active Jobs) read `row.batchId`/
 *   `row.planningGroup` directly (F1-F4); the station widgets resolve `batchId` through
 *   `common/reportOutcome.js#getJobBatchId` first and pass it along in this same shape (S141).
 * @param {string} intent One of `JOB_ACTION_INTENT` -- only `START` and `FINISH` ever resolve to a
 *   warning; every other intent (`PAUSE`, `GO_TO_EXECUTION`, `BACK_TO_PREPARATION`) is NONE,
 *   unconditionally.
 * @returns {string} one of `BATCH_WARNING_KIND`
 */
export const resolveBatchWarningKind = (row, intent) => {
  const hasBatch = isFilled(row?.batchId)

  if (intent === JOB_ACTION_INTENT.FINISH) {
    return hasBatch ? BATCH_WARNING_KIND.FINISH_BATCH_MEMBER : BATCH_WARNING_KIND.NONE
  }

  if (intent === JOB_ACTION_INTENT.START) {
    if (hasBatch) return BATCH_WARNING_KIND.RESUME_BATCH
    return isFilled(row?.planningGroup) ? BATCH_WARNING_KIND.GROUP_SHORTCUT : BATCH_WARNING_KIND.NONE
  }

  return BATCH_WARNING_KIND.NONE
}

/**
 * S133/RN60 -- whether N1's `PLANNING_GROUP`-mode `startableCount` is enough to actually OFFER the
 * group shortcut. The caller must always ask Sun (D-I8/R-9); this only judges the NUMBER it comes
 * back with, never anything counted client-side.
 *
 * @param {number} [startableCount]
 * @returns {boolean}
 */
export const hasEnoughGroupMembersToOfferShortcut = (startableCount) => (startableCount ?? 0) >= MIN_GROUP_STARTABLE_COUNT

/**
 * Single Job Mode (the Operation widget's setting, published as `globalData.singleJobMode`): the two
 * warnings that would START SEVERAL jobs at once -- the group shortcut and the batch resume -- are
 * withheld while the mode is on, so the click falls into the plain individual start. The finish-member
 * warning is kept: finishing is precisely what the mode asks for before the next start.
 *
 * @param {string} kind one of `BATCH_WARNING_KIND`, as `resolveBatchWarningKind` returned it
 * @param {boolean} singleJobMode
 * @returns {string} one of `BATCH_WARNING_KIND`
 */
export const withholdBatchStartsInSingleJobMode = (kind, singleJobMode) => {
  if (singleJobMode !== true) return kind
  if (kind === BATCH_WARNING_KIND.GROUP_SHORTCUT || kind === BATCH_WARNING_KIND.RESUME_BATCH) {
    return BATCH_WARNING_KIND.NONE
  }
  return kind
}
